import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sale } from '@/models/sale';

interface MonthlySalesMainChartProps {
  sales: Sale[];
  selectedYear: string;
  onYearChange: (year: string) => void;
  years: number[];
}

const MonthlySalesMainChart: React.FC<MonthlySalesMainChartProps> = ({
  sales,
  selectedYear,
  onYearChange,
  years
}) => {
  // Process data to group sales by month for the selected year
  const monthlySalesData = useMemo(() => {
    // Create an array for all 12 months with initial values of 0
    const months = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      name: format(new Date(2024, i, 1), 'MMMM', { locale: es }),
      total: 0,
      count: 0
    }));

    // Sum sales by month for the selected year
    sales.forEach(sale => {
      const saleDate = new Date(sale.date);
      const saleYear = saleDate.getFullYear();

      // Solo procesar ventas del año seleccionado
      if (saleYear.toString() === selectedYear) {
        const monthIndex = saleDate.getMonth();
        // Actualizar el total del mes
        months[monthIndex].total += sale.amount;
        months[monthIndex].count += 1;
      }
    });
    return months;
  }, [sales, selectedYear]);

  // Format currency for tooltip
  const formatCurrency = (value: number) => {
    return value.toLocaleString('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2
    });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip bg-card border border-border rounded-md shadow-md p-3">
          <p className="font-medium text-foreground">{`Mes: ${label}`}</p>
          <p className="text-muted-foreground">{`Total Ventas: ${formatCurrency(data.total)}`}</p>
        </div>
      );
    }
    return null;
  };

  const chartConfig = {
    sales: {
      label: "Ventas",
      color: "#6E59A5"
    }
  };

  return (
    <Card className="bg-card border-border overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-lg font-bold text-foreground">Ventas Totales por Mes</CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Año:</span>
            <Select value={selectedYear} onValueChange={onYearChange}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="Seleccionar año" />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={monthlySalesData} 
              margin={{
                top: 10,
                right: 30,
                left: 30,
                bottom: 70
              }}
              maxBarSize={50}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="name" 
                interval={0}
                height={60}
                tickLine={false}
                axisLine={true}
                tick={props => (
                  <g transform={`translate(${props.x},${props.y})`}>
                    <text
                      x={0}
                      y={0}
                      dy={16}
                      textAnchor="end"
                      fill="#666"
                      transform="rotate(-45)"
                    >
                      {props.payload.value}
                    </text>
                  </g>
                )}
              />
              <YAxis 
                tickFormatter={value => `$${value / 1000}k`}
                tick={{ fontSize: 12 }}
                axisLine={true}
                tickLine={true}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="total" 
                name="Ventas" 
                fill="hsl(var(--warning))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlySalesMainChart;
